import { AfterViewInit, Component, NgZone, OnDestroy, QueryList, ViewChildren } from '@angular/core';

import { NgxGpAutocompleteModule } from '@angular-magic/ngx-gp-autocomplete';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';
import { MenuItem } from 'primeng/api';

import { filter, map, startWith, switchMap, take, tap } from 'rxjs';
import { GoogleHelperService } from 'src/app/services/google-helper.service';

import { selectLocationDetailsWizardPrompt } from 'src/app/shared/location-details-wizard/location-details-wizard.selectors';

import { scrollIntoViewAndHighlight } from 'src/app/utilities/element.util';
import { SubSink } from 'subsink';

import { FreyaCommonModule } from '../../../freya-common/freya-common.module';
import { JOB_LOCATION_TYPES, LocationType } from '../../../global.constants';
import { strToTitleCase } from '../../../js';
import { LocationDetailsWizardComponent } from '../../../shared/location-details-wizard/location-details-wizard.component';
import { locationDetailsWizardFeature } from '../../../shared/location-details-wizard/location-details-wizard.feature';
import { SharedModule } from '../../../shared/shared.module';

import { JobCreateLocationsComponent } from '../../job-create/job-create-locations/job-create-locations.component';
import { selectCanChangeArea } from '../../job-edit/job-edit-state/job-edit.selectors';
import {
  jobToolFeature,
  selectJob,
  selectJobInCurrentZone,
  selectJobLoading,
} from '../../job-tool.reducer';
import { TravelMapComponent } from '../travel-map/travel-map.component';
import { TravelTimeComponent } from '../travel-time/travel-time.component';

import { OverviewLocationDetailsComponent } from './overview-location-details/overview-location-details.component';
import { OverviewLocationEditorComponent } from './overview-location-editor/overview-location-editor.component';
import { JobLocationsOverviewActions } from './overview-locations.actions';
import { overviewLocationsFeature } from './overview-locations.feature';
import {
  selectActiveJobLocation,
  selectActiveLocationEditor,
  selectCanCalculateDistances,
  selectOrderedLocations,
  selectSectionSaveButtonDisabledMessage,
} from './overview-locations.selectors';
import { StreetViewMapComponent } from './street-view-map/street-view-map.component';

@Component({
  selector: 'app-overview-locations',
  standalone: true,
  imports: [
    FreyaCommonModule,
    SharedModule,
    TravelMapComponent,
    TravelTimeComponent,
    JobCreateLocationsComponent,
    NgxGpAutocompleteModule,
    OverviewLocationDetailsComponent,
    OverviewLocationEditorComponent,
    LocationDetailsWizardComponent,
    StreetViewMapComponent,
  ],
  templateUrl: './overview-locations.component.html',
  styleUrl: './overview-locations.component.scss'
})
export class OverviewLocationsComponent implements AfterViewInit, OnDestroy {

  @ViewChildren(LocationDetailsWizardComponent) wizardList: QueryList<LocationDetailsWizardComponent>;

  subs = new SubSink();

  private _observer: IntersectionObserver;

  _selectAddLocationOptions = createSelector(
    selectJob,
    overviewLocationsFeature.selectLocationEditors,
    (job, editors) => {
      return JOB_LOCATION_TYPES
      .filter((lt) => {

        if (job?.locations?.some((l) => l.locationType === lt.key)) {
          return false;
        }

        if (lt.key in editors) {
          return false;
        }

        return true;
      })
      .map((lt) => ({
        ...lt,
        label: strToTitleCase(lt.label),
        command: () => {
          this.store.dispatch(this.actions.addableLocationTypeSelected({ locationType: lt.key }));
        }
      }));
    }
  );

  public viewModel$ = this.store.select(createSelector({
    job: selectJob,
    addLocationOptions: this._selectAddLocationOptions,
    addLocationButtonDisabled: createSelector(
      this._selectAddLocationOptions,
      selectJobInCurrentZone,
      (opts, jobInCurrentZone) => !jobInCurrentZone || opts.length === 0,
    ),
    canCalculateDistances: selectCanCalculateDistances,
    distancesLoading: jobToolFeature.selectDistancesLoading,
    locations: createSelector(
      selectOrderedLocations,
      overviewLocationsFeature.selectCalculateDistancesOutput,
      (orderedLocations, distances) => orderedLocations.map((l, i) => {

        return {
          locationType: l.locationType,
          distance: distances.distances[i],
        }
      }),
    ),
    hasLocations: createSelector(
      selectOrderedLocations,
      (locationTypes) => locationTypes.length > 0
    ),
    totalDistance: createSelector(
      selectOrderedLocations,
      overviewLocationsFeature.selectCalculateDistancesOutput,
      (orderedLocations, { distances, distanceUnits }) => {
        let estimatedTime = 0;
        let distance = 0;

        if (orderedLocations.length < 2 || !distances.length) {
          return { estimatedTime, distance, distanceUnits, locationsMissing: true };
        }

        for (let i = 0; i < orderedLocations.length - 1; i++) {
          const routeDistance = distances[i];
          estimatedTime += routeDistance?.estimatedTime ?? 0;
          distance += routeDistance?.distance ?? 0;
        }

        return {
          estimatedTime,
          distance,
          distanceUnits,
          locationsMissing: false,
        };
      }
    ),
    activeLocationType: overviewLocationsFeature.selectActiveLocationType,
    view: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      locationDetailsWizardFeature.selectLocationType,
      (activeLocationType, wizardLocationType) =>
        wizardLocationType && activeLocationType === wizardLocationType ? 'wizard' : 'form',
    ),
    locationDetailsWizardPrompt: selectLocationDetailsWizardPrompt,
    formattedLocationType: createSelector(
      overviewLocationsFeature.selectActiveLocationType,
      (locationType) => JOB_LOCATION_TYPES.find((lt) => lt.key === locationType)?.label || locationType,
    ),
    openInGoogleMapsButton: createSelector(
      selectActiveLocationEditor,
      (editor) => ({
        disabled: !editor?.selectedLocation,
        tooltip: !editor?.selectedLocation ? 'Select a location to open in Google Maps' : 'Open in Google Maps',
      }),
    ),
    openInGoogleSearchButton: createSelector(
      selectActiveLocationEditor,
      (editor) => ({
        disabled: !editor?.selectedLocation,
        tooltip: !editor?.selectedLocation ? 'Select a location to search in Google' : 'Search in Google',
      }),
    ),
    openInZillowButton: createSelector(
      selectActiveLocationEditor,
      (editor) => {
        if (!editor?.selectedLocation) {
          return { disabled: true, tooltip: 'Select a location to open in Zillow' };
        }
        const hasCoordinates = Boolean(
          editor?.selectedLocation?.coordinates?.latitude &&
          editor?.selectedLocation?.coordinates?.longitude
        );
        return {
          disabled: !hasCoordinates,
          tooltip: hasCoordinates ? 'Open in Zillow' : 'We have no coordinates for this location',
        };
      },
    ),
    jobNotInZone: createSelector(selectJobInCurrentZone, (inZone) => !inZone),
    actionsMenuItems: createSelector(
      selectActiveJobLocation,
      selectJobInCurrentZone,
      (jobLocation, jobInCurrentZone): MenuItem[] => [
        {
          label: 'Delete Location',
          icon: 'pi pi-trash',
          disabled: !jobInCurrentZone || !jobLocation,
          command: () => this.store.dispatch(JobLocationsOverviewActions.deleteLocationButtonClicked()),
        },
      ],
    ),
    jobLoading: selectJobLoading,
    canChangeArea: selectCanChangeArea,
    showAddLocationLink: selectJobInCurrentZone,
    saveButtonState: createSelector(
      selectSectionSaveButtonDisabledMessage('Locations'),
      jobToolFeature.selectLocationsMutating,
      (disabledMessage, locationsSaving) => {

        if (disabledMessage) {
          return {
            label: 'Save',
            icon: 'pi pi-check',
            disabled: true,
            tooltip: disabledMessage,
          }
        }

        if (locationsSaving) {
          return {
            label: 'Saving...',
            icon: 'pi pi-spin pi-spinner',
            disabled: true,
          };
        }

        return {
          label: 'Save',
          icon: 'pi pi-check',
          disabled: false,
        };
      }
    ),
    saveDockButtonDisabled: createSelector(
      overviewLocationsFeature.selectLocationEditors,
      (locationEditors) => {

        if (!locationEditors.dock?.selectedLocation) {
          return true;
        }

        return !locationEditors.dock?.locationModified;
      },
    ),
    dockAddress: createSelector(
      overviewLocationsFeature.selectLocationEditors,
      (locationEditors) => locationEditors.dock?.address || '',
    ),
    changeAreaState: createSelector(
      selectJob,
      overviewLocationsFeature.selectServiceArea,
      selectCanChangeArea,
      selectJobInCurrentZone,
      (job, resolvedServiceArea, canChangeArea, jobInCurrentZone) => {

        const jobIsShared = !jobInCurrentZone;

        const hasArea = job?.zone?.type === 'area';

        const hasNewPendingArea = Boolean(resolvedServiceArea) && job?.zone?.id !== resolvedServiceArea.id;

        const willChangeArea = hasNewPendingArea && canChangeArea;

        let header: string;
        let tooltip: string;
        let showPendingTag = false;

        if (hasArea && !willChangeArea) {
          header = `${job?.zone?.name}`;
        }

        if (hasArea && willChangeArea) {
          header = `${job?.zone?.name} → ${resolvedServiceArea.name}`;
          showPendingTag = true;
        }

        if (!hasArea && willChangeArea) {
          header = `${resolvedServiceArea.name}`;
          showPendingTag = true;
        }

        if (!header) {
          header = 'No Area';
        }

        if (jobIsShared) {
          tooltip = 'This job can only be edited from its original zone';
        } else if (!canChangeArea) {
          tooltip = 'Remove all transactions to change';
        }

        return {
          header,
          buttonLabel: hasArea ? 'Change Area' : 'Set Area',
          disabled: jobIsShared || willChangeArea || !canChangeArea,
          tooltip,
          showPendingTag,
        }
      }
    ),
  }));

  public actions = JobLocationsOverviewActions;

  constructor(
    public store: Store,
    public googleHelper: GoogleHelperService,
    private ngZone: NgZone,
  ) { }

  ngAfterViewInit(): void {
    this._focusWizard();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();

    if (this._observer) {
      this._observer.disconnect();
    }
  }

  public trackByLocationType(index: number, locationType: { locationType: LocationType; time: number }) {
    return locationType.locationType ?? index;
  }

  private _focusWizard() {

    const wizardRendered$ = this.wizardList.changes.pipe(
      startWith(this.wizardList),
      filter((wizards?: QueryList<LocationDetailsWizardComponent>) => Boolean(wizards) && wizards.length > 0),
      map((wizards) => wizards.first),
    );

    this.subs.sink = wizardRendered$.pipe(
      concatLatestFrom(() => this.store.select(overviewLocationsFeature.selectShouldFocusWizard)),
      filter(([ _wizard, shouldFocus ]) => shouldFocus),
      switchMap(([ wizard ]) => this.ngZone.onStable.pipe(
        take(1),
        tap(() => {

          this._observer?.disconnect();

          this._observer = scrollIntoViewAndHighlight(wizard.el.nativeElement, {
            onHighlighted: () => this.store.dispatch(JobLocationsOverviewActions.wizardFocused()),
          });
        }),
      )),
    ).subscribe();
  }

}
